import { Box, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { ActionButton } from "../../../../common/components/functional/actionBanner/styled";
import RejectedMessageDialog from "../../../../common/components/functional/rejectedMessageDialog";
import { EXCLUSIVITIES_PAGE_SIZE } from "../../../../common/components/functional/pagination/constants";
import { fetchCustomerCompanyByOrgId } from "../../../customer/customerOrganizationsSlice";
import { WorkLocation } from "../../../customer/types/queries";
import { fetchOverlappableAssignmentsByTalentId } from "../../../talentProfile/Employment/AssignmentAdditionalInfo/AssignmentsSlice";
import { isSelectionAllowedByExclusivities } from "../../../talentProfile/talentProfileSummary/talentSkillSet/talentIntroducedToCustomer/talentIntroduceToCustomerDialog/exclusivityMatching";
import { TalentExclusivity } from "../../../talents/talentAPI";
import { fetchExclusivitiesByTalentId } from "../../../talents/talentsSlice";
import { OrderEdit } from "../../orderAPI";
import { FulfillmentStatus } from "../../orderAPI";
import { selectOrderRowById } from "../../orderRowsDetailedSlice";
import { Candidate } from "../../TalentsStatusAPI";
import {
  approveIntroducedTalents,
  cancelTalentRejection,
  cancelTalentWithdrawal,
  cancelIntroduceToCustomer,
  pickCandidates,
  rejectCandidates,
  sendRejectedMessageToCandidates,
  selectAllPickedCandidates,
  selectAllRecommendedCandidates,
  selectAllTalentStatusJobAdApplicants,
} from "../../talentsStatusSlice";
import { fetchTalentRejectedDefaultMessageTemplate } from "../../../settings/messages/messagesSlice";
import { SelectedTalent } from "../../types/editOrder";
import {
  CandidateStatusAction,
  TalentStatusDialogContent,
} from "../constants";
import { mapActionsFor } from "./actionMapper";
import { TalentActionBannerContainer } from "./styles";
import { FLOATING_CHAT_BUTTON_BOTTOM_RAISED } from "../../../messaging/messagingSlice";
import { useFloatingChatButtonBottom } from "../../../messaging/useFloatingChatButtonBottom";
import { WorkOfferType } from "./workAgreement/helpers";

type Props = {
  selectedTalents: SelectedTalent[];
  bundleId: string;
  onDialogOpen: (content: TalentStatusDialogContent) => void;
  openWorkAgreementTypeDialog: () => void;
  openExclusivityBlockingDialog: (
    talent: SelectedTalent,
    exclusivities: TalentExclusivity[],
  ) => void;
};

export const TalentActionBanner = ({
  selectedTalents,
  bundleId,
  onDialogOpen,
  openWorkAgreementTypeDialog,
  openExclusivityBlockingDialog,
}: Props) => {
  useFloatingChatButtonBottom(FLOATING_CHAT_BUTTON_BOTTOM_RAISED);

  const [actions, setActions] = useState<CandidateStatusAction[]>([]);
  const [isRejectedMessageDialogOpen, setIsRejectedMessageDialogOpen] =
    useState(false);

  const { t } = useTranslation(["order", "common"]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const order: OrderEdit = useAppSelector((state) =>
    selectOrderRowById(state, bundleId!!),
  );

  // TODO: create selector for selecting all candidates
  const pickedCandidates = useAppSelector(selectAllPickedCandidates);
  const recommendedCandidates = useAppSelector(selectAllRecommendedCandidates);
  const jobAdApplicants = useAppSelector(selectAllTalentStatusJobAdApplicants);

  const checkExclusivityConflict = async ({
    talentId,
    workLocationId,
    customerOrgId,
    workTitleId,
  }: {
    talentId: string;
    workLocationId: string;
    customerOrgId: string;
    workTitleId?: string | null;
  }): Promise<{ isBlocked: boolean; exclusivities: TalentExclusivity[] } | null> => {
    const exclusivitiesAction = await dispatch(
      fetchExclusivitiesByTalentId({ talentId, pageSize: EXCLUSIVITIES_PAGE_SIZE }),
    );

    if (!isFulfilled(exclusivitiesAction)) {
      return null;
    }

    const exclusivities: TalentExclusivity[] = exclusivitiesAction.payload.data;
    const workLocation = { id: workLocationId, customerOrgId } as WorkLocation;

    const companyOnlyExclusivities = exclusivities.filter(
      (ex) => !ex.customerOrganizationId && !ex.workLocationId,
    );

    let resolvedCompanyId: string | null = null;
    if (companyOnlyExclusivities.length > 0) {
      const companyAction = await dispatch(
        fetchCustomerCompanyByOrgId({ customerOrgId }),
      );
      if (isFulfilled(companyAction)) {
        resolvedCompanyId = companyAction.payload.customerCompanyId;
      }
    }

    const isAllowed = isSelectionAllowedByExclusivities({
      exclusivities,
      workLocation,
      selectedWorkTitleIds: workTitleId ? [workTitleId] : [],
      resolvedCompanyId,
    });

    return { isBlocked: !isAllowed, exclusivities };
  };

  const openRejectedMessageDialog = () => {
    setIsRejectedMessageDialogOpen(true);
  };

  const closeRejectedMessageDialog = () => {
    setIsRejectedMessageDialogOpen(false);
  };

  const fetchTemplateRejectedMessage = useCallback(async () => {
    const action = await dispatch(
      fetchTalentRejectedDefaultMessageTemplate({
        bundleId,
      }),
    );
    return isFulfilled(action) ? action.payload : "";
  }, [dispatch, bundleId]);

  const sendRejectedMessage = (message: string) => {
    dispatch(
      sendRejectedMessageToCandidates({
        bundleId,
        talentIds: selectedTalents.map((talent) => talent.id),
        message,
      }),
    );
    closeRejectedMessageDialog();
  };

  const createWorkOffer = async () => {
    const talent = selectedTalents[0];
    const workOfferState = {
      talent: {
        // workOffer can be sent to only one talent at a time
        id: talent.id,
        fullName: talent.fullName,
      },
      type: WorkOfferType.New,
    };

    if (talent.exclusivityEntryIds?.length) {
      const result = await checkExclusivityConflict({
        talentId: talent.id,
        workLocationId: order.workLocationId,
        customerOrgId: order.customerOrgId,
        workTitleId: order.taskSpec.workTitleId,
      });
      if (result?.isBlocked) {
        openExclusivityBlockingDialog(talent, result.exclusivities);
        return;
      }
    }

    const overlapCheck = await dispatch(
      fetchOverlappableAssignmentsByTalentId({
        bundleId,
        talentIds: [talent.id],
      }),
    );
    if (isFulfilled(overlapCheck)) {
      if (overlapCheck.payload.length) {
        openWorkAgreementTypeDialog();
      } else {
        navigate(`/home/orders/${bundleId}/offer`, {
          state: workOfferState,
        });
      }
    }
  };

  const pick = () => {
    const payload = {
      bundleId,
      talentIds: selectedTalents.map((talent) => talent.id),
    };

    dispatch(pickCandidates(payload));
  };

  const cancelIntroduction = () => {
    const payload = {
      bundleId,
      talentIds: selectedTalents.map((talent) => talent.id),
    };

    dispatch(cancelIntroduceToCustomer(payload));
  };

  const cancelTalentWithdrew = () => {
    const payload = {
      bundleId,
      talentIds: selectedTalents.map((talent) => talent.id),
    };

    dispatch(cancelTalentWithdrawal(payload));
  };

  const cancelRejection = () => {
    const payload = {
      bundleId,
      talentIds: selectedTalents.map((talent) => talent.id),
    };

    dispatch(cancelTalentRejection(payload));
  };

  const setCustomerApproval = (approved: boolean) => {
    dispatch(
      approveIntroducedTalents({
        bundleId,
        talentIds: selectedTalents.map((talent) => talent.id),
        approved,
      }),
    );
  };

  const getAllowedActionsForSelection = useCallback(
    (
      selectedTalents: SelectedTalent[],
      candidates: Candidate[],
    ): CandidateStatusAction[] => {
      if (!selectedTalents.length) {
        return [];
      }

      const candidatesById = new Map(candidates.map((c) => [c.talentId, c]));
      const selectedCandidates = selectedTalents
        .map((talent) => candidatesById.get(talent.id))
        .filter((candidate): candidate is Candidate => !!candidate);

      if (selectedCandidates.length !== selectedTalents.length) {
        return [];
      }

      const offeredCount = candidates.filter((candidate) => candidate.offered?.success)
        .length;
      const fulfillmentStatus = order.status?.fulfillmentStatus;
      const orderFulfilled =
        fulfillmentStatus === FulfillmentStatus.Full ||
        fulfillmentStatus === FulfillmentStatus.FullFinished ||
        fulfillmentStatus === FulfillmentStatus.PartialFinished ||
        fulfillmentStatus === FulfillmentStatus.NoneFinished;
      const orderCancelled = !!order.cancelReason;
      const orderedAmountOffered = offeredCount >= order.amount;

      const actionsPerCandidate = selectedCandidates.map((candidate) =>
        mapActionsFor({
          candidate,
          orderCancelled,
          orderFulfilled,
          orderedAmountOffered,
        }),
      );

      const sharedActions = actionsPerCandidate.reduce<CandidateStatusAction[]>(
        (shared, candidateActions) =>
          shared.filter((action) => candidateActions.includes(action)),
        actionsPerCandidate[0] ?? [],
      );

      // Work offer is single-talent flow in UI.
      if (selectedTalents.length > 1) {
        return sharedActions.filter(
          (action) => action !== CandidateStatusAction.SEND_WORK_OFFER,
        );
      }

      return sharedActions;
    },
    [
      order.amount,
      order.cancelReason,
      order.status?.fulfillmentStatus,
    ],
  );

  useEffect(() => {
    const candidates = [
      ...pickedCandidates,
      ...recommendedCandidates,
      ...jobAdApplicants.map((applicant) => ({
        talentId: applicant.id,
        firstName: applicant.firstName,
        lastName: applicant.lastName,
        status: applicant.status,
        applieadAt: applicant.appliedAt,
      })),
    ];

    const actions = getAllowedActionsForSelection(selectedTalents, candidates);

    setActions(actions);
  }, [
    selectedTalents,
    getAllowedActionsForSelection,
    pickedCandidates,
    recommendedCandidates,
    jobAdApplicants,
  ]);

  const getButtonProps = (action: CandidateStatusAction) => {
    const buttonProps = {
      [CandidateStatusAction.PICK]: {
        color: "primary" as "primary",
        onClick: () => pick(),
      },
      [CandidateStatusAction.UNPICK]: {
        color: "primary" as "primary",
        onClick: () => console.log("REMOVE_FROM_PICKED"),
      },
      [CandidateStatusAction.ASK_FOR_INTEREST]: {
        color: "primary" as "primary",
        onClick: () => onDialogOpen(TalentStatusDialogContent.ASK_FOR_INTEREST),
      },
      [CandidateStatusAction.CHOOSE_FOR_INTRODUCTION]: {
        color: "primary" as "primary",
        onClick: () =>
          onDialogOpen(TalentStatusDialogContent.CHOOSE_FOR_INTRODUCTION),
      },
      [CandidateStatusAction.CHOOSE_FOR_INTERVIEW]: {
        color: "primary" as "primary",
        onClick: () => onDialogOpen(TalentStatusDialogContent.CHOOSE_FOR_INTERVIEW),
      },
      [CandidateStatusAction.SEND_WORK_OFFER]: {
        color: "primary" as "primary",
        onClick: createWorkOffer,
      },
      [CandidateStatusAction.CANCEL_INTRODUCTION]: {
        color: "error" as "error",
        onClick: cancelIntroduction,
      },
      [CandidateStatusAction.CUSTOMER_APPROVED]: {
        color: "success" as "success",
        onClick: () => setCustomerApproval(true),
      },
      [CandidateStatusAction.CUSTOMER_REJECTED]: {
        color: "error" as "error",
        onClick: () => onDialogOpen(TalentStatusDialogContent.CUSTOMER_REJECTED),
      },
      [CandidateStatusAction.NOT_SUITABLE]: {
        color: "error" as "error",
        onClick: () => onDialogOpen(TalentStatusDialogContent.NOT_SUITABLE),
      },
      [CandidateStatusAction.SEND_REJECTED_MESSAGE]: {
        color: "primary" as "primary",
        onClick: openRejectedMessageDialog,
      },
      [CandidateStatusAction.CANCEL_REJECTION]: {
        color: "error" as "error",
        onClick: cancelRejection,
      },
      [CandidateStatusAction.WITHDREW]: {
        color: "primary" as "primary",
        onClick: () => onDialogOpen(TalentStatusDialogContent.WITHDREW),
      },
      [CandidateStatusAction.CANCEL_WITHDRAWAL]: {
        color: "error" as "error",
        onClick: cancelTalentWithdrew,
      },
    };

    return buttonProps[action];
  };

  // remove when actions are fully implemented
  const isNotImplemented = (action: CandidateStatusAction) =>
    action === CandidateStatusAction.UNPICK;

  return (
    <>
      <TalentActionBannerContainer>
        {actions.map((action, index) => (
          <Fragment key={action}>
            <ActionButton
              key={action}
              variant="text"
              sx={{
                ...(isNotImplemented(action) && {
                  color: "magenta",
                  display: "none",
                }),
              }}
              {...getButtonProps(action)}
            >
              <Typography variant="header" fontSize="16px" fontWeight={700}>
                {t(action)}
              </Typography>
            </ActionButton>
            <Box
              hidden={
                index === actions.length - 1 ||
                // Next action is not implemented
                isNotImplemented(actions[index + 1]) ||
                // Current action is not implemented
                isNotImplemented(action)
              }
              width="2px"
              height="24px"
              bgcolor="#000A441C"
            />
          </Fragment>
        ))}
      </TalentActionBannerContainer>

      <RejectedMessageDialog
        open={isRejectedMessageDialogOpen}
        title={t("sendRejectedMessage")}
        onClose={closeRejectedMessageDialog}
        onSend={sendRejectedMessage}
        fetchTemplateMessage={fetchTemplateRejectedMessage}
      />
    </>
  );
};
